export type Provider = 'google' | 'microsoft';
export type UserRole = 'admin' | 'user';
export type LoginFailureStage = 'sso' | 'brand-login';
export type CreativesSurface = 'explore' | 'elements-explorer';
export type CreativeDetailTab = 'overview' | 'scores' | 'tags';
export type AskAlisonSource = 'input' | 'follow-up';
export type CompetitionTab = 'creatives' | 'elements';
export type CompetitionElementsMode = 'all' | 'most-used';
export type SignalSurface = 'panel' | 'home';
export type ReportCreativeSection = 'top' | 'under' | 'fatigued' | 'hook';

export interface AnalyticsIdentity {
  userId: number;
  email: string;
  name: string | null;
  organizationId: number;
  organizationName: string | null;
}

export interface Events {
  'Auth - Login Started': { method: Provider };
  'Auth - Login': { method?: Provider; clientId: number; brandName: string };
  'Auth - Login Failed': {
    stage: LoginFailureStage;
    reason: string;
    method?: Provider;
    clientId?: number;
    brandName?: string;
  };
  'Auth - Logout': never;
  'Invite - Sent': {
    invitedEmail: string;
    role: UserRole;
    integrationCount: number;
  };
  'Invite - Resent': { invitedEmail: string };
  'Share - Sent': {
    workflowId: string;
    runId: string;
    recipientCount: number;
    sendType: 'manual' | 'scheduled';
  };
  'Invite - Revoked': { invitedEmail: string };
  'Invite - Accepted': never;
  'Filter - Scope Changed': {
    brands: string[];
    platforms: string[];
    accounts: string[];
    creativeTypes: string[];
    rangePreset: string;
    rangeFrom?: string;
    rangeTo?: string;
  };
  'Filter - Changed': { bar: string; filter: string; values: string[] };
  'Filter - Added': { filter: string };
  'Filter - Removed': { filter: string };
  'Competition Filter - Added': { filter: string };
  'Competition Filter - Removed': { filter: string };
  'Competition - Tab Changed': { tab: CompetitionTab };
  'Competition Creatives - View Changed': { view: 'grid' | 'table' };
  'Competition Creatives - Sort Changed': { sort: 'newest' | 'longestRunning' };
  'Competition Creatives - Tagged Only Toggled': { enabled: boolean };
  'Competition Creatives - CSV Exported': never;
  'Competition Creatives - More Loaded': never;
  'Competition Creative - Opened': { creativeId: string };
  'Competition Elements - Mode Changed': { mode: CompetitionElementsMode };
  'Competition Elements - Scene Toggled': { scene: string; active: boolean };
  'Competition Elements - Category Toggled': { category: string; selected: boolean };
  'Competition Elements - Status Toggled': { status: string; selected: boolean };
  'Competition Elements - CSV Exported': never;
  'Competition Elements - Creatives Opened': { feature: string; tag: string | null };
  'Competition Creative Detail - Navigated': { direction: 'previous' | 'next' };
  'Filter - Metric Added': never;
  'Filter - Metric Removed': never;
  'Page Viewed': { path: string; routeId: string };
  'Explore - Open Creative': { creativeId: string };
  'Explore - Change View': { view: 'grid' | 'table' };
  'Explore - Sort': { field: string; direction: 'asc' | 'desc' };
  'Explore - Reset Sort': never;
  'Explore - Export CSV': never;
  'Explore - Load More': never;
  'Elements Explorer - Select Category': { category: string };
  'Elements Explorer - Filter Tab': { tab: string };
  'Elements Explorer - Toggle How It Works': { expanded: boolean };
  'Metrics Picker - Apply': { surface: CreativesSurface; metrics: string[] };
  'Top Metrics - Customize': { surface: CreativesSurface; metrics: string[] };
  'Top Metrics - Open Graph': { surface: CreativesSurface; metric: string };
  'Top Metrics - Change Aggregation': {
    surface: CreativesSurface;
    metric: string;
    aggregation: 'average' | 'total';
  };
  'Creative Detail - Navigate': { direction: 'previous' | 'next' };
  'Creative Detail - Change Tab': { tab: CreativeDetailTab };
  'Creative Detail - See Breakdown': never;
  'Creative Detail - Copy Preview': never;
  'Creative Detail - Change Comparison': { comparison: string };
  'Creative Detail - Change Trend Metric': { metric: string };
  'Creative Detail - Filter Tags': { filter: 'element' | 'tag'; values: string[] };
  'Creative Detail - Toggle All Tags': { expanded: boolean };
  'Creative Detail - Select Score Component': { component: string };
  'Ask Alison - Ask': { source: AskAlisonSource; question: string };
  'Ask Alison - Ask Failed': never;
  'Ask Alison - Click Workflow': { workflowId: string };
  'Dashboard - View Tooltip': { tile: string };
  'Workflow - Opened': { workflowId: string; goal?: string };
  'Workflow - Chain Back': { workflowId: string; toActionType: string };
  'Workflow - Run Started': { workflowId: string; runId: string };
  'Workflow - Run Completed': { workflowId: string; runId: string };
  'Workflow - Run Failed': { workflowId: string; runId: string; reason: string };
  'Workflow - Next Action Chosen': { workflowId: string; fromRunId: string };
  'Workflow - Brief Viewed': { workflowId: string };
  'Workflow - Brief Finalized': { workflowId: string; briefs: number };
  'Workflow - Brief Edited': { workflowId: string };
  'Workflow - Full Analysis Opened': { workflowId: string };
  'Workflow - Creative Opened': { workflowId: string; source?: 'result_page' | 'full_report' };
  'Workflow - Recommendations Expanded': { creativeId: string };
  'Outcomes - Outcome Opened': { kind: string };
  'Outcomes - Source Opened': { kind: string };
  'Outcomes - Filter Changed': { filter: string };
  'Outcomes - Grouping Toggled': { grouped: boolean };
  'Tour - Started': { source: 'auto' | 'help' };
  'Tour - Step Viewed': { step: string; index: number };
  'Tour - Skipped': { step: string; index: number };
  'Tour - Completed': never;
  'Automation - Created': { automationId: string; workflowId: string; frequency: string };
  'Automation - Edited': { automationId: string };
  'Automation - Paused': { automationId: string };
  'Automation - Resumed': { automationId: string };
  'Automation - Deleted': { automationId: string };
  'Automation - Run Now': { automationId: string };
  'Automation Occurrence - Succeeded': { automationId: string; occurrenceId: string };
  'Automation Occurrence - Failed': { automationId: string; occurrenceId: string };
  'Automation Occurrence - Empty': { automationId: string; occurrenceId: string };
  'Automation Occurrence - Skipped': { automationId: string; occurrenceId: string };
  'Signal - Opened': { actionType: string; severity: string | null; surface: SignalSurface };
  'Signal - Dismissed': { actionType: string };
  'Signal - Restored': { actionType: string };
  'Reports - Creative Opened': { creativeId: string; section: ReportCreativeSection };
  'Reports - KPI Changed': { kpi: string };
  'Reports - Campaigns Changed': { campaignCount: number };
}

export type EventName = keyof Events;

export const EVENT_NAMES = [
  'Auth - Login Started',
  'Auth - Login',
  'Auth - Login Failed',
  'Auth - Logout',
  'Invite - Sent',
  'Invite - Resent',
  'Share - Sent',
  'Invite - Revoked',
  'Invite - Accepted',
  'Filter - Scope Changed',
  'Filter - Changed',
  'Filter - Added',
  'Filter - Removed',
  'Competition Filter - Added',
  'Competition Filter - Removed',
  'Competition - Tab Changed',
  'Competition Creatives - View Changed',
  'Competition Creatives - Sort Changed',
  'Competition Creatives - Tagged Only Toggled',
  'Competition Creatives - CSV Exported',
  'Competition Creatives - More Loaded',
  'Competition Creative - Opened',
  'Competition Elements - Mode Changed',
  'Competition Elements - Scene Toggled',
  'Competition Elements - Category Toggled',
  'Competition Elements - Status Toggled',
  'Competition Elements - CSV Exported',
  'Competition Elements - Creatives Opened',
  'Competition Creative Detail - Navigated',
  'Filter - Metric Added',
  'Filter - Metric Removed',
  'Page Viewed',
  'Explore - Open Creative',
  'Explore - Change View',
  'Explore - Sort',
  'Explore - Reset Sort',
  'Explore - Export CSV',
  'Explore - Load More',
  'Elements Explorer - Select Category',
  'Elements Explorer - Filter Tab',
  'Elements Explorer - Toggle How It Works',
  'Metrics Picker - Apply',
  'Top Metrics - Customize',
  'Top Metrics - Open Graph',
  'Top Metrics - Change Aggregation',
  'Creative Detail - Navigate',
  'Creative Detail - Change Tab',
  'Creative Detail - See Breakdown',
  'Creative Detail - Copy Preview',
  'Creative Detail - Change Comparison',
  'Creative Detail - Change Trend Metric',
  'Creative Detail - Filter Tags',
  'Creative Detail - Toggle All Tags',
  'Creative Detail - Select Score Component',
  'Ask Alison - Ask',
  'Ask Alison - Ask Failed',
  'Ask Alison - Click Workflow',
  'Dashboard - View Tooltip',
  'Workflow - Opened',
  'Workflow - Chain Back',
  'Workflow - Run Started',
  'Workflow - Run Completed',
  'Workflow - Run Failed',
  'Workflow - Next Action Chosen',
  'Workflow - Brief Viewed',
  'Workflow - Brief Finalized',
  'Workflow - Brief Edited',
  'Workflow - Full Analysis Opened',
  'Workflow - Creative Opened',
  'Workflow - Recommendations Expanded',
  'Outcomes - Outcome Opened',
  'Outcomes - Source Opened',
  'Outcomes - Filter Changed',
  'Outcomes - Grouping Toggled',
  'Tour - Started',
  'Tour - Step Viewed',
  'Tour - Skipped',
  'Tour - Completed',
  'Automation - Created',
  'Automation - Edited',
  'Automation - Paused',
  'Automation - Resumed',
  'Automation - Deleted',
  'Automation - Run Now',
  'Automation Occurrence - Succeeded',
  'Automation Occurrence - Failed',
  'Automation Occurrence - Empty',
  'Automation Occurrence - Skipped',
  'Signal - Opened',
  'Signal - Dismissed',
  'Signal - Restored',
  'Reports - Creative Opened',
  'Reports - KPI Changed',
  'Reports - Campaigns Changed',
] as const satisfies readonly EventName[];

type MissingEventName = Exclude<EventName, (typeof EVENT_NAMES)[number]>;
const _allEventsListed: [MissingEventName] extends [never] ? true : MissingEventName = true;
void _allEventsListed;
